import { Stack, Box } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { InlineFieldEditor } from "components/lib-ui/parser/InlineFieldEditor";
import GeneralUtilityService from "services/GeneralUtilityService";
import ParsedDataRow from "components/lib-ui/parser/ParsedDataRow";
import { useForm } from "react-hook-form";
import { useCandidateValidation } from "hooks/useCandidate";
import SocialMediaURL from "services/SocialMediaURL.service";
import useNotifications from "hooks/useNotifications";
import { useDispatch } from "react-redux";
import {
    validateParsedCandidate,
    setEditableData,
} from "reducers/candidate/parsedProfileDataSlice";
import { emailValidator } from "validators";

const titleStyles = {
    fontSize: "1rem",
    textTransform: "uppercase",
    fontWeight: "600",
};

export const PrimaryData = ({ data }) => {
    const { generalInfo, existingUserData = {} } = data;
    const { handleSubmit, setValue } = useForm();
    const { renderErrorNotification } = useNotifications();
    const { validateCandidate, isLoading } = useCandidateValidation();

    const dispatch = useDispatch();

    const parsedLocation = GeneralUtilityService.isObjectEmpty(
        GeneralUtilityService.clearDeepEmptyKeys(generalInfo?.location || {}),
    )
        ? null
        : generalInfo.location;

    const fields = [
        {
            title: "Name",
            fieldName: "firstName",
            origin: "generalInfo",
            type: "text",
        },
        {
            title: "Surname",
            fieldName: "lastName",
            origin: "generalInfo",
            type: "text",
        },
        {
            title: "Phone number",
            fieldName: "phone",
            origin: "generalInfo",
            type: "phone",
        },
        {
            title: "Email",
            fieldName: "email",
            origin: "generalInfo",
            type: "email",
        },
        {
            title: "LinkedIn url",
            fieldName: "linkedinProfile",
            origin: "generalInfo",
            type: "text",
        },
        {
            title: "Location",
            fieldName: "location",
            origin: "generalInfo",
            type: "location",
        },
    ];

    const validateLinkedInProfile = (linkedinInput) => {
        const { isValid, message } =
            SocialMediaURL.validateLinkedinInput(linkedinInput);
        if (!isValid) {
            renderErrorNotification(message[0]);
            return false;
        }
        return SocialMediaURL.extractLinkedinId(linkedinInput);
    };

    const validateEmail = (email) => {
        const { isValid, errorMessage } = emailValidator(email);
        if (!isValid) {
            renderErrorNotification(errorMessage);
            return false;
        }
        return true;
    };

    const existingPrimaryData = !!existingUserData?.generalInfo;

    const mergeDiffDataWithExistingData = (formData, existingData) => {
        const { firstName, lastName, linkedinProfile } =
            existingData?.generalInfo || {};

        return {
            ...formData,
            firstName: formData.firstName || firstName,
            lastName: formData.lastName || lastName,
            linkedinProfile:
                formData.linkedinProfile || linkedinProfile
                    ? `${SocialMediaURL.LINKEDIN_PROFILE_BASE_URL}/${linkedinProfile}`
                    : null,
        };
    };

    const getCallingCodeId = () =>
        existingUserData?.generalInfo?.calling_code?.id ?? undefined;

    const handleLinkedInProfile = (formData) => {
        if (!formData?.linkedinProfile) return formData;

        const processedProfile = validateLinkedInProfile(
            formData.linkedinProfile,
        );

        if (!processedProfile) return null;

        return { ...formData, linkedinProfile: processedProfile };
    };

    const onSubmit = async (formData) => {
        if (existingPrimaryData) {
            formData = mergeDiffDataWithExistingData(
                formData,
                existingUserData,
            );
        }

        let updatedFormData = handleLinkedInProfile(formData);

        if (!updatedFormData) return;

        // The backend system requires only the ID of the calling code
        // instead of the entire object.
        updatedFormData.calling_code_id = getCallingCodeId();

        if (formData.email && !validateEmail(formData.email)) return;

        const clearedFormData =
            GeneralUtilityService.clearEmptyKeys(updatedFormData);

        const checkIfShouldValidate = (isExisting) => {
            if (isExisting) {
                return true;
            } else return validateCandidate(clearedFormData);
        };

        dispatch(
            setEditableData({
                data: { generalInfo: clearedFormData },
            }),
        );

        const shouldParseCandidate =
            await checkIfShouldValidate(existingPrimaryData);

        if (shouldParseCandidate) {
            dispatch(validateParsedCandidate());
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing="1px">
                <ParsedDataRow
                    title="Primary Information"
                    titleStyles={titleStyles}
                />
                {fields.map((field) => {
                    const isLinkedinField =
                        field.fieldName === "linkedinProfile";
                    const composeLinkedin = `${
                        SocialMediaURL.LINKEDIN_PROFILE_BASE_URL
                    }/${generalInfo[field.fieldName]}`;
                    return (
                        <ParsedDataRow
                            title={field.title}
                            key={field.fieldName}
                        >
                            <Box>
                                <InlineFieldEditor
                                    fieldName={field.fieldName}
                                    defaultValue={
                                        field.type === "location"
                                            ? parsedLocation
                                            : isLinkedinField &&
                                                generalInfo[field.fieldName]
                                              ? composeLinkedin
                                              : generalInfo[field.fieldName]
                                    }
                                    type={field.type}
                                    hookFormSetValue={setValue}
                                    disabled={field.disabled}
                                    diffView={existingPrimaryData}
                                />
                            </Box>
                        </ParsedDataRow>
                    );
                })}
            </Stack>

            <Stack spacing={2} pt={6} direction="row" justifyContent="flex-end">
                <LoadingButton
                    color="primary"
                    variant="contained"
                    type="submit"
                    loading={isLoading}
                    py={2}
                    sx={{
                        borderRadius: "100px",
                        textTransform: "none",
                        fontWeight: "400",
                        paddingLeft: 4,
                        paddingRight: 4,
                    }}
                >
                    Check user and continue
                </LoadingButton>
            </Stack>
        </Box>
    );
};

export default PrimaryData;
